import {
	AlertCircle,
	Ban,
	CheckCircle,
	FileQuestion,
	Info,
	ServerOff,
	XCircle,
} from "lucide-react";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface ResultProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
	status?: "success" | "error" | "info" | "warning" | "404" | "403" | "500";
	title?: React.ReactNode;
	subTitle?: React.ReactNode;
	extra?: React.ReactNode;
	icon?: React.ReactNode;
}

const Result = forwardRef<HTMLDivElement, ResultProps>(
	({ className, status = "info", title, subTitle, extra, icon, children, ...props }, ref) => {
		const statusConfig = {
			success: {
				icon: CheckCircle,
				color: "text-success-500",
				defaultTitle: "成功",
			},
			error: {
				icon: XCircle,
				color: "text-error-500",
				defaultTitle: "出错了",
			},
			info: {
				icon: Info,
				color: "text-primary-500",
				defaultTitle: "提示",
			},
			warning: {
				icon: AlertCircle,
				color: "text-warning-500",
				defaultTitle: "警告",
			},
			"404": {
				icon: FileQuestion,
				color: "text-gray-400",
				defaultTitle: "404",
			},
			"403": {
				icon: Ban,
				color: "text-warning-500",
				defaultTitle: "403",
			},
			"500": {
				icon: ServerOff,
				color: "text-error-500",
				defaultTitle: "500",
			},
		};

		const config = statusConfig[status];
		const IconComponent = config.icon;

		const renderIcon = () => {
			if (icon) return icon;

			// 特殊状态页面使用更大的图标
			if (status === "404" || status === "403" || status === "500") {
				return (
					<div className="flex flex-col items-center gap-3">
						<IconComponent className={cn("h-20 w-20", config.color)} strokeWidth={1} />
						<span className="text-5xl font-bold text-gray-300 dark:text-gray-600">{status}</span>
					</div>
				);
			}

			return <IconComponent className={cn("h-16 w-16", config.color)} strokeWidth={1.5} />;
		};

		return (
			<div
				ref={ref}
				className={cn(
					"flex flex-col items-center justify-center py-12 px-6 text-center",
					className,
				)}
				{...props}
			>
				<div className="mb-6">{renderIcon()}</div>

				{title && (
					<h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
				)}

				{subTitle && <p className="text-gray-500 dark:text-gray-400 max-w-md mb-6">{subTitle}</p>}

				{extra && <div className="flex gap-2 flex-wrap justify-center">{extra}</div>}

				{children && <div className="mt-6 w-full">{children}</div>}
			</div>
		);
	},
);

Result.displayName = "Result";

export { Result };
